import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { track } from "@/lib/analytics";
import { SERVICES, IDEAL_ARTIST, GENRES, IMAGES } from "@/lib/data";

export const ArtistDevelopment = () => {
  return (
    <section id="partnership" className="relative py-28 lg:py-36 bg-[#050505]" data-testid="partnership-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <p className="text-[12px] tracking-[0.3em] uppercase text-white/40 mb-5">Artist Development · Creative Partnerships</p>
          <h2 className="font-heading uppercase tracking-tight text-white text-4xl lg:text-6xl leading-[0.95]">
            More than beats — a <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">creative partner</span>
          </h2>
          <p className="text-white/55 mt-6 leading-relaxed text-[15px] max-w-xl">
            I work with serious independent artists who want to develop a sound, a catalogue and a real creative direction. This is the high-touch side of what I do.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/8] mb-6"
          data-testid="partnership-banner"
        >
          <img src={IMAGES.group} alt="TAPSS with artists in the studio" className="w-full h-full object-cover object-[50%_32%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 via-transparent to-transparent" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-2xl border border-white/10 bg-white/[0.02] p-8 lg:p-10"
          >
            <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-7">What we build together</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {SERVICES.map((s, i) => (
                <div key={s} className="flex items-center gap-3 py-2 border-b border-white/5" data-testid={`service-${i}`}>
                  <Check size={16} className="text-white/60 shrink-0" />
                  <span className="text-white/75 text-[15px]">{s}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Ideal artist */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-8 lg:p-10 flex flex-col"
          >
            <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-7">Who this is for</p>
            <ul className="space-y-3 flex-1">
              {IDEAL_ARTIST.map((a, i) => (
                <li key={a} className="text-white/70 text-[15px] flex gap-3" data-testid={`ideal-${i}`}>
                  <span className="font-serif-accent italic text-gold">{String(i + 1).padStart(2, "0")}</span>
                  {a}
                </li>
              ))}
            </ul>
            <a
              href="#sessions"
              onClick={() => track("partnership_click", "artist_development")}
              data-testid="apply-cta"
              className="group mt-8 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gold text-black text-[13px] tracking-[0.12em] uppercase font-medium hover:-translate-y-1 transition-transform duration-300"
            >
              Apply to Work Together <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>

        {/* Genres */}
        <div className="mt-12">
          <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-5">Priority genres</p>
          <div className="flex flex-wrap gap-3">
            {GENRES.filter((g) => g !== "Other").map((g) => (
              <span key={g} className="px-5 py-2.5 rounded-full border border-white/12 text-white/70 text-[13px] tracking-[0.06em] hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`genre-${g}`}>
                {g}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArtistDevelopment;
